import { openDB, IDBPDatabase } from 'idb';
import { useCallback, useEffect, useRef } from 'react';
import type { CompanyIntel } from '@/types';

const DB_NAME = 'ProfitibullRecruiterIntel';
const DB_VERSION = 1;
const STORE_NAME = 'companyIntel';

interface DBRecord {
  companyId: string;
  intel: CompanyIntel;
}

export function useIndexedDB() {
  const dbRef = useRef<IDBPDatabase | null>(null);

  // Initialize database
  const initDB = useCallback(async () => {
    if (dbRef.current) return dbRef.current;

    const db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(database) {
        if (!database.objectStoreNames.contains(STORE_NAME)) {
          database.createObjectStore(STORE_NAME, { keyPath: 'companyId' });
        }
      },
    });

    dbRef.current = db;
    return db;
  }, []);

  // Load all intel data
  const loadAllIntel = useCallback(async (): Promise<Record<string, CompanyIntel>> => {
    const db = await initDB();
    const records = await db.getAll(STORE_NAME) as DBRecord[];
    
    const data: Record<string, CompanyIntel> = {};
    records.forEach((item) => {
      data[item.companyId] = item.intel;
    });
    
    return data;
  }, [initDB]);

  // Save intel for a company
  const saveIntel = useCallback(
    async (companyId: string, intel: CompanyIntel): Promise<void> => {
      const db = await initDB();
      await db.put(STORE_NAME, { companyId, intel });
    },
    [initDB]
  );

  // Get intel for a specific company
  const getIntel = useCallback(
    async (companyId: string): Promise<CompanyIntel | null> => {
      const db = await initDB();
      const record = (await db.get(STORE_NAME, companyId)) as DBRecord | undefined;
      return record?.intel || null;
    },
    [initDB]
  );

  // Delete intel for a company
  const deleteIntel = useCallback(
    async (companyId: string): Promise<void> => {
      const db = await initDB();
      await db.delete(STORE_NAME, companyId);
    },
    [initDB]
  );

  // Clear all intel data
  const clearAll = useCallback(async (): Promise<void> => {
    const db = await initDB();
    await db.clear(STORE_NAME);
  }, [initDB]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (dbRef.current) {
        dbRef.current.close();
      }
    };
  }, []);

  return {
    initDB,
    loadAllIntel,
    saveIntel,
    getIntel,
    deleteIntel,
    clearAll,
  };
}
